// UserMessage organism (D57, D60, D61).
//
// Sprint ai-elements-rendering (spec §3.1) — the bubble shell is now the
// AI Elements `Message`/`MessageContent` pair. Extensions carried on top
// of the template (zero-feature-loss contract):
//   - full-width block (D57) — overrides the template's right-aligned
//     `ml-auto max-w-[95%]` messenger bubble; Santi explicitly rejected
//     the right-bubble style (mockup review 2026-05-02).
//   - sticky-per-turn positioning (see below), 3-line clamp + fade
//     overlay + expand-on-tap (D60/D61), Edit/Resend actions,
//     attachment chips, `.md` DocPathChips.
//
// Sprint sticky-message-section-stacking-cursor-parity — the bubble is
// always `position: sticky; top: var(--user-sticky-top, 0px); z-20`.
// Its containing block is the per-turn `<section data-turn-id>` rendered
// by ConversationList — when that section's bottom edge reaches the
// viewport top (i.e. the next user message's section begins), the
// browser naturally pushes this bubble out, and the next section's
// user message takes over the sticky slot. No JS sticky machinery,
// no component swap, no IntersectionObserver.

import { useState, type ReactNode } from 'react';
import { useExpandable } from '../../hooks/useExpandable';
import { useTranslation } from '../../i18n';
import { Icon } from '../atoms/Icon';
import type { StoredAttachment } from '../../types';
import { AttachmentChip } from '../AttachmentChip';
import { detectMdPaths } from '../../lib/detectMdPaths';
import { DocPathChip } from '../DocPathChip';
import { useDocViewer } from '../../hooks/useDocViewer';
import { Message, MessageContent } from '../ai-elements/message';

function renderTextWithChips(
  text: string,
  onOpen: (p: string) => void,
): ReactNode {
  const matches = detectMdPaths(text);
  if (matches.length === 0) return text;
  const parts: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.start > cursor) parts.push(text.slice(cursor, m.start));
    parts.push(
      <DocPathChip
        key={`${m.start}-${m.path}-${i}`}
        path={m.path}
        onOpen={onOpen}
      />,
    );
    cursor = m.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

export interface UserMessageProps {
  text: string;
  onEdit?: (text: string) => void;
  onResend?: (text: string) => void;
  // Sprint 8 — chips persisted on the user message row, surfaced above the
  // text on every render. Empty/null on legacy rows.
  attachments?: StoredAttachment[] | null;
  // Sprint attachments-anthropic-parity Phase D — agent id forwarded into
  // every chip so the image-route ones can build the
  // `/api/agents/:agentId/attachments/:chipId/raw` thumbnail URL.
  // Optional: if omitted (subagent panel, archived agent edge cases) the
  // chip degrades to the generic icon — no 404 surfaced to the founder.
  agentId?: string;
}

// Oficina Virtual F1 (spec 001 T12) — COO wake prompts are injected into
// the chat prefixed with this marker. They are machine-generated noise for
// the founder, so they render collapsed as one discrete line. STRICT
// startsWith match: no other message may take this branch (regression
// guard — normal user messages keep the exact pre-T12 render path).
const WAKE_MARKER = '⟦WAKE⟧';

// Dedicated component (not an inline branch inside UserMessage's JSX) so
// the normal path's hooks (useExpandable, useDocViewer) never run for wake
// rows and hook order stays stable in both components.
function WakeUserMessage({ text }: { text: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const summary = text.slice(WAKE_MARKER.length).trimStart().split('\n')[0] ?? '';

  return (
    <div className="w-full max-w-full py-0.5" data-testid="wake-message">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="w-full text-left rounded px-2 py-1 text-caption text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-colors duration-100 truncate"
        data-testid="wake-message-toggle"
      >
        {t('office.wake.collapsed', { summary })}
      </button>
      {expanded && (
        <div
          className="mt-1 rounded border border-border bg-secondary/40 px-3 py-2 text-caption text-muted-foreground whitespace-pre-wrap break-words"
          data-testid="wake-message-full"
        >
          {text}
        </div>
      )}
    </div>
  );
}

export function UserMessage(props: UserMessageProps) {
  if (props.text.startsWith(WAKE_MARKER)) {
    return <WakeUserMessage text={props.text} />;
  }
  return <RegularUserMessage {...props} />;
}

function RegularUserMessage({ text, onEdit, onResend, attachments, agentId }: UserMessageProps) {
  const { t } = useTranslation();
  const { expanded, toggle, overflows, ref } = useExpandable({ maxLines: 3 });
  const { openDoc } = useDocViewer();

  return (
    <Message
      from="user"
      className="sticky top-[var(--user-sticky-top,0px)] z-20 w-full max-w-full"
    >
      <MessageContent
        className="relative w-full cursor-pointer gap-0 transition-colors duration-100 hover:bg-secondary/80"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        aria-expanded={expanded}
        data-testid="user-message"
      >
        {attachments && attachments.length > 0 && (
          <div
            className="mb-2 flex flex-wrap gap-2"
            data-testid="user-message-attachments"
            onClick={(e) => e.stopPropagation()}
          >
            {attachments.map((a) => (
              <AttachmentChip
                key={a.id}
                id={a.id}
                route={a.route}
                name={a.displayName}
                size={a.size}
                state="uploaded"
                staticOnBubble
                agentId={agentId}
              />
            ))}
          </div>
        )}
        <div
          ref={ref}
          className={`text-chat leading-relaxed text-foreground whitespace-pre-wrap break-words ${
            !expanded && overflows ? 'line-clamp-3' : ''
          }`}
        >
          {renderTextWithChips(text, openDoc)}
        </div>

        {/* Fade overlay when clamped (D61 — gradient only, no hint row) */}
        {!expanded && overflows && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8 rounded-b-lg"
            style={{
              background:
                'linear-gradient(to bottom, transparent, hsl(var(--secondary)))',
            }}
            aria-hidden="true"
          />
        )}

        {expanded && (
          <div
            className="mt-3 flex items-center gap-2 border-t border-border pt-2"
            data-testid="user-message-actions"
            onClick={(e) => e.stopPropagation()}
          >
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(text)}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-caption text-foreground/74 hover:bg-accent hover:text-foreground transition-colors duration-100"
              >
                <Icon name="Pencil" size={12} aria-hidden="true" />
                {t('message.edit')}
              </button>
            )}
            {onResend && (
              <button
                type="button"
                onClick={() => onResend(text)}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-caption text-foreground/74 hover:bg-accent hover:text-foreground transition-colors duration-100"
              >
                <Icon name="CornerUpRight" size={12} aria-hidden="true" />
                {t('message.resend')}
              </button>
            )}
          </div>
        )}
      </MessageContent>
    </Message>
  );
}
